import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSaveOrderServicesMutation, CheckoutOrderFragment } from '@websky/graphql';
import {
	CartService,
	CartServiceAeroexpress,
	CartServiceGds,
	CartServiceInsurance,
	OnDeleteItemType
} from '../../../../Cart/types';
import { getAeroexpress, getOrder, getTravellers } from '../../../store/order/selectors';
import { setServiceAction } from '../../../store/selectedServices/actions';
import { useInsurance } from '../../../../CheckoutAdditionalService/components/Insurance/hooks';
import { getPassengerState } from '../../../store/passengers/selectors';
import { getPassengerStateCompatible } from '../../../store/selectedServices/selectors';
import { fillOrder } from '../../../store/order/actions';
import { isServicesSaving } from '../../../store/servicesIsSaving/selectors';
import { getCartServicesToDelete } from '../../../utils';
import { getSeatNumbersToRemove } from '@shared/lib/seat-map';

export const useCart = () => {
	const order = useSelector(getOrder),
		dispatch = useDispatch();

	const [saveOrderServicesMutation, { data, loading }] = useSaveOrderServicesMutation();

	const travellers = useSelector(getTravellers);
	const passengers = useSelector(getPassengerState);
	const aeroexpress = useSelector(getAeroexpress);
	const servicesSaving = useSelector(isServicesSaving);
	const passengersWithSeats = useSelector(getPassengerStateCompatible);

	const { onSave } = useInsurance({
		orderId: order.id,
		insurances: order.additionalServices.insurances,
		insurancePrograms: order.additionalServices.insurances?.insurancePrograms,
		passengers,
		onSaveServices: saveOrderServicesMutation
	});

	const aeroexpressSelectedIds = aeroexpress?.selectedTrips?.map(trip => trip.id);

	useEffect(() => {
		if (!loading && data && data.SaveOrderServices) {
			dispatch(fillOrder(data?.SaveOrderServices as CheckoutOrderFragment));
		}
	}, [data, loading]);

	const onDeleteItem: OnDeleteItemType = useCallback(
		(type, service) => {
			// GdsServices
			if (
				type === CartService.Baggage ||
				type === CartService.Meal ||
				type === CartService.Extra ||
				type === CartService.Transfer
			) {
				const gdsService = service as CartServiceGds;

				const segmentIds = gdsService.allSegments
					? order.flight.segments.map(segment => segment.segment.id)
					: [gdsService.segmentId];

				segmentIds.forEach(segmentId => {
					dispatch(
						setServiceAction({
							allowedSegments: gdsService.allowedSegments,
							passengerId: gdsService.passengerId,
							segmentId,
							serviceId: gdsService.serviceId,
							count: gdsService.count ?? 0
						})
					);
				});
			} else if (type === CartService.Seats) {
				const gdsService = service as CartServiceGds;
				const passenger = passengersWithSeats.find(p => p.id === gdsService.passengerId);

				const segmentIds = gdsService.allSegments
					? order.flight.segments.map(segment => segment.segment.id)
					: [gdsService.segmentId];

				segmentIds.forEach(segmentId => {
					const seatNumbers = getSeatNumbersToRemove(passenger?.seats ?? [], segmentId, gdsService.serviceId);

					seatNumbers.forEach(seatNumber => {
						dispatch(
							setServiceAction({
								allowedSegments: gdsService.allowedSegments,
								passengerId: gdsService.passengerId,
								segmentId,
								serviceId: seatNumber,
								count: 0
							})
						);
					});
				});
			} else if (type === CartService.BusinessLounge || type === CartService.SMS) {
				const gdsService = service as CartServiceGds;
				const servicesToDelete = getCartServicesToDelete(gdsService, travellers);

				servicesToDelete.forEach(service => {
					dispatch(setServiceAction(service));
				});
			} else if (type === CartService.Insurances) {
				onSave(
					order.additionalServices.insurances.selectedInsurances
						.filter(selected => selected.insuranceProgram.code !== (service as CartServiceInsurance).code)
						.map(selected => selected.insuranceProgram.code)
				);
			} else if (type === CartService.Aeroexpress) {
				const aeroexpress = service as CartServiceAeroexpress;

				saveOrderServicesMutation({
					variables: {
						params: {
							id: order.id,
							aeroexpress: {
								selectedTrips: aeroexpressSelectedIds.filter(id => id !== aeroexpress.tripId)
							}
						}
					}
				});
			}
		},
		[order, travellers, passengersWithSeats, aeroexpressSelectedIds, onSave, dispatch]
	);

	return {
		onDeleteItem,
		loading: loading || servicesSaving,
		isLoadingOrderServices: loading
	};
};
